/**
 * Router configuration for custom query parameter handling in the MAR application.
 *
 * This module provides custom parsing and stringifying functions for URL query parameters
 * to handle array parameters with a specific format where arrays are passed as comma-separated
 * values with the key suffix '[]'.
 *
 * @example
 * // URL: ?tags[]=red,blue,green&category=art
 * // Parsed result: { tags: ['red', 'blue', 'green'], category: 'art' }
 */

import type { RouterConfig } from 'nuxt/schema';

const DELIMETER = ',';

/**
 * Custom router configuration that overrides default query parameter handling.
 * Uses custom parsing and stringifying functions to support array parameters
 * with comma-separated values.
 */
const HmRouterConfig = {
	parseQuery: customParse,
	stringifyQuery: customStringify,
} satisfies RouterConfig;

export default HmRouterConfig;

/**
 * Custom query parameter parsing function.
 *
 * Parses URL query strings with special handling for array parameters.
 * Parameters ending with '[]' are treated as arrays where the value
 * is split by commas to create an array of strings.
 *
 * @param queryString - The raw query string from the URL (without leading '?')
 * @returns Parsed query object with proper array handling
 *
 * @example
 * customParse('tags[]=red,blue,green&category=art')
 * // Returns: { tags: ['red', 'blue', 'green'], category: 'art' }
 *
 * customParse('single=value&empty=')
 * // Returns: { single: 'value', empty: '' }
 */
function customParse(queryString: string): Record<string, any> {
	const result: Record<string, any> = {};
	// Split query string into key-value pairs, removing leading '?' if present
	const pairs = queryString.replace(/^\?/, '').split('&').filter(Boolean);

	for (const pair of pairs) {
		const [rawKey, rawVal] = pair.split('=');
		const key = decodeURIComponent(rawKey || '');
		const val = decodeURIComponent(rawVal || '');

		if (key.endsWith('[]')) {
			// Handle array parameters: remove '[]' suffix and split value by comma
			const baseKey = key.slice(0, -2);
			result[baseKey] = val.split(DELIMETER).map(item => item.trim());
		}
		else {
			// Handle single value parameters
			result[key] = val;
		}
	}

	return result;
}

/**
 * Custom query parameter stringifying function.
 *
 * Converts a query object back to a URL query string with special handling
 * for array values. Arrays are converted to comma-separated values with
 * the key suffixed with '[]'.
 *
 * @param query - The query object to convert to string
 * @returns URL-encoded query string
 *
 * @example
 * customStringify({ tags: ['red', 'blue', 'green'], category: 'art' })
 * // Returns: 'tags[]=red,blue,green&category=art'
 *
 * customStringify({ single: 'value', empty: '', nullValue: null })
 * // Returns: 'single=value&empty=' (null values are omitted)
 */
function customStringify(query: Record<string, any> | undefined): string {
	const parts: string[] = [];

	for (const key in query) {
		const value = query[key];

		if (Array.isArray(value)) {
			// Convert arrays to comma-separated values with '[]' suffix
			parts.push(`${encodeURIComponent(key)}[]=${value.map(encodeURIComponent).join(DELIMETER)}`);
		}
		else if (value !== undefined && value !== null) {
			// Handle single values, skip null/undefined
			parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
		}
	}

	const result = parts.join('&');
	return result;
}
